// ============================================================================
// SUPPLY CRATE ENTITY - Dropped loot container
// ============================================================================

import { EntityId, Vector2, WeaponId } from '@/shared/types';
import { generateId } from '@/shared/utils';
import { CollisionBody, ColliderType, CollisionLayers } from '@/engine/CollisionSystem';

export enum SupplyType {
  Weapon = 'weapon',
  HealthKit = 'health_kit',
  Credits = 'credits',
  Shield = 'shield',
  Grenade = 'grenade',
  BombCall = 'bomb_call',
  MissileStrike = 'missile_strike',
}

export interface SupplyCrateState {
  id: EntityId;
  position: Vector2;
  supplyType: SupplyType;
  weaponId?: WeaponId;
  amount?: number;
  active: boolean;
  claimed: boolean;
  dropTime: number;
  landTime: number | null;
  parachuteActive: boolean;
  altitude: number;
}

export class SupplyCrate {
  public readonly id: EntityId;
  public position: Vector2;
  public prevPosition: Vector2;
  public supplyType: SupplyType;
  public weaponId?: WeaponId;
  public amount?: number; // For credits, health amount, etc
  public active: boolean;
  public claimed: boolean;
  public dropTime: number;
  public landTime: number | null;
  public parachuteActive: boolean;
  public altitude: number; // Current height above ground
  private fallSpeed: number = 50; // Pixels per second descent rate
  private readonly size: number = 30; // Crate size

  constructor(
    position: Vector2,
    supplyType: SupplyType,
    dropTime: number,
    initialAltitude: number,
    weaponId?: WeaponId,
    amount?: number
  ) {
    this.id = generateId('crate');
    this.position = { ...position };
    this.prevPosition = { ...position };
    this.supplyType = supplyType;
    if (weaponId !== undefined) this.weaponId = weaponId;
    if (amount !== undefined) this.amount = amount;
    this.active = true;
    this.claimed = false;
    this.dropTime = dropTime;
    this.landTime = null;
    this.parachuteActive = true;
    this.altitude = initialAltitude;
  }

  update(dt: number, time: number): boolean {
    if (!this.active || this.claimed) return false;

    this.prevPosition = { ...this.position };

    // Descend with parachute
    if (this.parachuteActive && this.altitude > 0) {
      this.altitude -= this.fallSpeed * dt / 1000;
      
      if (this.altitude <= 0) {
        this.altitude = 0;
        this.parachuteActive = false;
        this.landTime = time;
        return true; // Just landed
      }
    }
    return false;
  }

  claim(): void {
    this.claimed = true;
    this.active = false;
  }

  isLanded(): boolean {
    return this.altitude <= 0 && !this.parachuteActive;
  }

  getCollisionBody(): CollisionBody {
    // Only create collision body when landed
    if (!this.isLanded()) {
      return {
        id: this.id,
        position: this.position,
        velocity: { x: 0, y: 0 },
        collider: {
          type: ColliderType.Circle,
          offset: { x: 0, y: 0 },
          radius: 0, // No collision while falling
        },
        layer: CollisionLayers.PROJECTILE,
        mask: 0,
        isStatic: true,
        isTrigger: true,
        userData: this,
      };
    }

    return {
      id: this.id,
      position: this.position,
      velocity: { x: 0, y: 0 },
      collider: {
        type: ColliderType.Circle,
        offset: { x: 0, y: 0 },
        radius: this.size / 2,
      },
      layer: CollisionLayers.PROJECTILE,
      mask: CollisionLayers.PLAYER,
      isStatic: true,
      isTrigger: true,
      userData: this,
    };
  }

  toState(): SupplyCrateState {
    const state: SupplyCrateState = {
      id: this.id,
      position: this.position,
      supplyType: this.supplyType,
      active: this.active,
      claimed: this.claimed,
      dropTime: this.dropTime,
      landTime: this.landTime,
      parachuteActive: this.parachuteActive,
      altitude: this.altitude,
    };
    if (this.weaponId !== undefined) state.weaponId = this.weaponId;
    if (this.amount !== undefined) state.amount = this.amount;
    return state;
  }

  static fromState(state: SupplyCrateState): SupplyCrate {
    const crate = new SupplyCrate(
      state.position,
      state.supplyType,
      state.dropTime,
      state.altitude,
      state.weaponId,
      state.amount
    );
    crate.active = state.active;
    crate.claimed = state.claimed;
    crate.landTime = state.landTime;
    crate.parachuteActive = state.parachuteActive;
    return crate;
  }
}
